import styled, { keyframes } from 'styled-components';
import { Badge } from '@shared/ui/Badge';

export const DEADLINE_KIND_LABELS: Record<string, string> = {
  claim_response: 'Срок ответа на претензию',
  statute_of_limitations: 'Срок исковой давности',
  procedural: 'Процессуальный срок',
  answer: 'Срок ответа',
};

export const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const Grid = styled.dl`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  margin: 0;
  font-size: 14px;

  & > dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  & > dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

export const SummaryBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const DraftWrap = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)};
`;

export const DraftBadge = styled.div`
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border-radius: ${({ theme }) => theme.radius.pill};
`;

export const DraftText = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const HitlNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-style: italic;
`;

export const SeverityBadge = styled(Badge)<{ $sev: string }>`
  background: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger + '22'
      : $sev === 'major'
        ? theme.colors.status.warning + '22'
        : theme.colors.surface};
  color: ${({ $sev, theme }) =>
    $sev === 'critical'
      ? theme.colors.status.danger
      : $sev === 'major'
        ? theme.colors.status.warning
        : theme.colors.text.secondary};
  font-weight: 600;
`;

export const RiskCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

export const RiskHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const RiskBody = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const RiskAction = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  & > strong {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const RiskRefsRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const RefChip = styled.span<{ $found: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 11px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $found, theme }) =>
    $found ? theme.colors.status.success + '22' : theme.colors.status.warning + '22'};
  color: ${({ $found, theme }) =>
    $found ? theme.colors.status.success : theme.colors.status.warning};
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const AnalyzingBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.colors.primary}14 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.8s linear infinite;
    pointer-events: none;
  }
`;

export const AnalyzingTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;
  z-index: 1;
`;

export const AnalyzingHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  position: relative;
  z-index: 1;
  line-height: 1.5;
`;

export const LinkedContractCard = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const LinkedContractRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

export const ContractHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

export const ReanalyzeNotice = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  line-height: 1.45;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

export const MissingLinkNote = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

export const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.status.warning + '14'};
  border-radius: ${({ theme }) => theme.radius.md};
  flex-wrap: wrap;
`;

export const ConfirmText = styled.div`
  flex: 1;
  min-width: 220px;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ConfirmActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

export const INCOMING_CATEGORY_LABELS: Record<string, string> = {
  requirements: 'Требования',
  legal_basis: 'Правовое основание',
  calculations: 'Расчёт',
  evidence: 'Доказательства',
  procedural: 'Процессуальные пороки',
  defense: 'Наши контраргументы',
  risk: 'Риск проигрыша',
};

export const OUTGOING_CATEGORY_LABELS: Record<string, string> = {
  position: 'Правовая позиция',
  calculations: 'Расчёт',
  evidence: 'Доказательственная база',
  procedural: 'Процессуальные требования',
  formal: 'Формальные требования',
  counterarguments: 'Возражения контрагента',
};

export function categoryLabel(direction: string, cat: string): string {
  const map = direction === 'outgoing' ? OUTGOING_CATEGORY_LABELS : INCOMING_CATEGORY_LABELS;
  return map[cat] ?? cat;
}

export function fmtMoney(v: unknown): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return v.toLocaleString('ru-RU');
}

export function getStr(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

export function getBool(meta: Record<string, unknown>, key: string): boolean | null {
  const v = meta[key];
  return typeof v === 'boolean' ? v : null;
}

export function getNum(meta: Record<string, unknown>, key: string): number | null {
  const v = meta[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
